import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { CustomDropZone, DropZoneHelperText, getInitialFiles, Grid, Typography } from 'web-library';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { attachedFilesService } from '../../../../../api';
import { GET_ACCESS_ARRANGEMENT_SUBMITTED_DRAFT } from '../../../../../api/candidate/access-arrangement/@candidate/queries-services/keys';
import { ApplicationType } from '../../../../../api/candidate/config/model';
import { GET_WITHDRAWAL_DRAFT } from '../../../../../api/candidate/exam/@candidate/queries-services/keys';
import { useGetApplicationFilesConfig } from '../../../../../api/document/attachedFiles/queries-services/queries';
import AttachedFiles from '../../../../../components/upload-documents/AttachedFiles';
import { FormIds } from '../../../../../modules/accessArrangment/components/candidate/SelectCondition/components/SelectConditionContent';
import { convertMB2Byte } from '../../../../../utils/FileUtils';
import { handleFileDelete } from './components/useUploadFile';

const useStyles = makeStyles()((theme) =>
({
  header: {
    marginBottom: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(4),
  },
  errString: {
    color: theme.color.state.stateError,
  },
}));

interface UploadDocumentsProps {
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  hasError?: boolean;
  isHeader?: boolean;
  submittedFiles?: number;
  applicationType: ApplicationType;
  draftFiles?: UploadedFile[];
  setDraftFiles?: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
}

const onFileUpload = (
  uploadedFiles: File[],
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>
) => {
  setFiles(
    uploadedFiles.map((file) => ({
      file,
      uploaded: false,
      hasError: false,
      pending: false,
    }))
  );
};

const UploadDocuments = ({
  files,
  setFiles,
  hasError,
  isHeader,
  submittedFiles,
  applicationType,
  draftFiles,
  setDraftFiles,
}: UploadDocumentsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const queryCache = useQueryClient();
  const [dropzoneKey, setDropzoneKey] = useState<string>('');

  const { mutate: deleteFileMutate } = attachedFilesService.all.useDeleteAppealArrangementFile(
    () => {
      queryCache.invalidateQueries(GET_ACCESS_ARRANGEMENT_SUBMITTED_DRAFT);
      queryCache.invalidateQueries(GET_WITHDRAWAL_DRAFT);
    }
  );

  const { data: applicationFilesConfig } = useGetApplicationFilesConfig(
    applicationType === ApplicationType.NON_NATIONAL_EXAM ? ApplicationType.EXAM : applicationType
  );

  const onUploadFileDelete = async (file: File, isDraft = false) => {
    // to handle file delete
    if (isDraft && draftFiles && setDraftFiles) {
      handleFileDelete(file, draftFiles, setDraftFiles, deleteFileMutate);
    } else {
      handleFileDelete(file, files, setFiles, deleteFileMutate);
    }

    setDropzoneKey(Date.now().toString());
  };

  const initialFiles = getInitialFiles(files);

  return (
    <>
      {isHeader && (
        <>
          <Typography
            variant="h3"
            id={FormIds.UPLOAD_DOCUMENTS_CONTAINER}
            className={`${classes.header} ${hasError ? classes.errString : ''}`}
          >
            {t('accessArrangement->>SelectCondition.UploadDocumentsHeader')}
          </Typography>
          <Typography
            variant="body1"
            className={`${classes.description} ${hasError ? classes.errString : ''}`}
          >
            {t('accessArrangement->>SelectCondition.UploadDocumentsDescription')}
          </Typography>
        </>
      )}

      {applicationFilesConfig && (
        <Grid xs={12} sm={12} md={12} lg={12}>
          <CustomDropZone
            filesLimit={applicationFilesConfig.fileNumLimit}
            acceptedFiles={applicationFilesConfig.uploadFileTypes}
            maxSize={convertMB2Byte(applicationFilesConfig.fileSizeLimit)}
            key={dropzoneKey}
            initialFiles={initialFiles}
            onChange={(f) => onFileUpload(f, setFiles)}
            onDelete={onUploadFileDelete}
          />
          <DropZoneHelperText
            acceptedFileTypes={applicationFilesConfig.uploadFileTypes}
            fileLimit={applicationFilesConfig.fileNumLimit}
            fileSizeLimit={applicationFilesConfig.fileSizeLimit}
          />
        </Grid>
      )}

      <AttachedFiles
        files={files}
        setFiles={setFiles}
        onUploadFileDelete={onUploadFileDelete}
        draftFiles={draftFiles}
        downloadableLink
      />
    </>
  );
};

export default UploadDocuments;
